import { useEffect, useId, useState, type ReactNode } from 'react';
import { GmailMark } from '../email/MailSurface';
import styles from './Integrations.module.css';
export function CalendarMark() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285f4" d="M3 3h18v18H3z" />
      <path fill="#fff" d="M6 6h12v12H6z" />
      <path fill="#34a853" d="M18 18h3l-3 3z" />
      <path fill="#fbbc04" d="M3 18h3v3H3z" />
      <text
        x="12"
        y="15"
        fill="#1a73e8"
        fontSize="8"
        textAnchor="middle"
        fontFamily="sans-serif"
      >
        31
      </text>
    </svg>
  );
}
export function IntegrationBar({
  provider,
  summary,
  action,
  children,
  initiallyOpen = false,
  title,
  description,
  onConnect,
  connecting = false,
  connectionDisabled = false,
  connectionError,
  expandable = true,
  refreshing = false,
  expandedAction,
  hideSummary = false,
  open: controlledOpen,
  onOpenChange,
  hidden = false,
  bare = false,
}: {
  provider: 'gmail' | 'google_calendar';
  summary: string;
  action?: ReactNode;
  children?: ReactNode;
  initiallyOpen?: boolean;
  title?: string;
  description?: string;
  onConnect?: (() => void) | undefined;
  connecting?: boolean;
  connectionDisabled?: boolean;
  connectionError?: string | undefined;
  expandable?: boolean;
  refreshing?: boolean;
  expandedAction?: ReactNode;
  hideSummary?: boolean;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  hidden?: boolean;
  bare?: boolean;
}) {
  const [internalOpen, setOpen] = useState(initiallyOpen),
    id = useId();
  const open = controlledOpen ?? internalOpen;
  const [hasOpened, setHasOpened] = useState(initiallyOpen);
  useEffect(() => { if (open) setHasOpened(true); }, [open]);
  const interactive=expandable;
  const Control=interactive?'button':'div';
  return (
    <section
      className={styles.section}
      hidden={hidden}
      data-bare={bare}
      data-expanded={expandable && open}
      data-setup={!!description}
      aria-label={title ?? (provider === 'gmail' ? 'Gmail' : 'Google Calendar')}
    >
      {!bare && <div className={styles.header}>
        <Control
          className={styles.toggle}
          data-interactive={interactive}
          type={interactive ? "button" : undefined}
          aria-label={title}
          aria-expanded={expandable ? open : undefined}
          aria-controls={expandable ? id : undefined}
          onClick={expandable ? () => { setHasOpened(true); setOpen(!open); onOpenChange?.(!open); } : undefined}
        >
          {provider === 'gmail' ? <GmailMark /> : <CalendarMark />}
          <span className={styles.label}>
            <strong>
              {title ?? (provider === 'gmail' ? 'Gmail' : 'Google Calendar')}
            </strong>
            {!hideSummary && <span className={styles.summary}>{refreshing ? provider === 'gmail' ? 'Syncing emails…' : 'Syncing meetings…' : description ?? summary}</span>}
          </span>
          {refreshing && <span className={styles.refreshSpinner} role="status" aria-label="Refreshing" />}
          {!refreshing && expandable && !(open && expandedAction) && (
            <svg
              className={styles.chevron}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m9 5 7 7-7 7" />
            </svg>
          )}
        </Control>
        {open && expandedAction && <div className={styles.setupHeaderAction}>{expandedAction}</div>}
        {!onConnect && action && (
          <div className={styles.headerAction}>{action}</div>
        )}
      </div>}
      {/* Retain mounted editors through collapse. */}
      {expandable && (
        <div id={id} className={styles.content} hidden={!open}>
          {(hasOpened || open) && <>
            {children}
            {onConnect && !bare && <button type="button" className={styles.connectButton} disabled={connecting || connectionDisabled} onClick={onConnect}>
              {connecting ? 'Opening Google…' : title ?? 'Connect'}
            </button>}
            {connectionError && <p className={styles.connectionError} role="alert">{connectionError}</p>}
          </>}
        </div>
      )}
    </section>
  );
}
